/**
 * ScheduleCPrint.jsx - IRS-style Schedule C Print Component
 * Renders a form that looks like the actual IRS Schedule C (Form 1040)
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Printer, X } from 'lucide-react';

export default function ScheduleCPrint({ user, taxYear, onClose }) {
  const [loading, setLoading] = useState(true);
  const [scheduleCTotals, setScheduleCTotals] = useState({});

  useEffect(() => {
    fetchData();
  }, [taxYear]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const year = parseInt(taxYear) || new Date().getFullYear();
      const startDate = `${year}-01-01`;
      const endDate = `${year}-12-31`;

      const res = await axios.get(
        `${API}/schedule-c-report?start_date=${startDate}&end_date=${endDate}`,
        { headers }
      );
      setScheduleCTotals(res.data.totals || {});
    } catch (error) {
      console.error('Failed to fetch Schedule C data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return <div className="fixed inset-0 z-50 bg-white flex items-center justify-center">Loading Schedule C data...</div>;
  }

  // Format currency
  const fmt = (val) => {
    const num = parseFloat(val) || 0;
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Get line value from report totals
  const g = (line) => scheduleCTotals[line] || 0;

  // Income lines
  const line1 = g('1');
  const line2 = g('2');
  const line3 = line1 - line2;
  const line4 = 0;
  const line5 = line3 - line4;
  const line6 = g('6');
  const line7 = line5 + line6;

  // Expense lines
  const line8 = g('8');
  const line9 = g('9');
  const line10 = g('10');
  const line11 = g('11');
  const line12 = g('12');
  const line13 = g('13');
  const line14 = g('14');
  const line15 = g('15');
  const line16a = g('16a');
  const line16b = g('16b');
  const line17 = g('17');
  const line18 = g('18');
  const line19 = g('19');
  const line20a = g('20a');
  const line20b = g('20b');
  const line21 = g('21');
  const line22 = g('22');
  const line23 = g('23');
  const line24a = g('24a');
  const line24b = g('24b');
  const line25 = g('25');
  const line26 = g('26');
  const line27a = g('27a');
  const line27b = g('27b');

  const line28 = line8 + line9 + line10 + line11 + line12 + line13 + line14 + line15 +
                 line16a + line16b + line17 + line18 + line19 + line20a + line20b +
                 line21 + line22 + line23 + line24a + line24b + line25 + line26 + line27a + line27b;
  const line29 = line7 - line28;
  const line30 = g('30');
  const line31 = line29 - line30;

  return (
    <div className="fixed inset-0 z-[100] bg-white overflow-auto" data-testid="irs-print-form">
      {/* Print/Close Buttons - Hidden when printing */}
      <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center z-10 print:hidden">
        <h2 className="text-lg font-bold">Schedule C Preview - Tax Year {taxYear}</h2>
        <div className="flex gap-2">
          <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
          <Button variant="outline" onClick={onClose}>
            <X className="w-4 h-4 mr-2" /> Close
          </Button>
        </div>
      </div>

      {/* IRS Form - This is what prints */}
      <div className="max-w-[8in] mx-auto p-6 bg-white print:p-4 print:max-w-none" style={{ fontSize: '11px', lineHeight: '1.3', color: '#000' }}>
        {/* Form Header */}
        <div className="border-2 border-black mb-2">
          <div className="flex">
            <div className="w-28 border-r border-black p-2 text-center">
              <div className="font-bold text-sm">SCHEDULE C</div>
              <div className="text-xs">(Form 1040)</div>
              <div className="text-[8px] mt-1">Department of the Treasury</div>
              <div className="text-[8px]">Internal Revenue Service</div>
            </div>
            <div className="flex-1 p-2 text-center">
              <div className="font-bold text-lg">Profit or Loss From Business</div>
              <div className="text-sm">(Sole Proprietorship)</div>
              <div className="text-[9px] mt-1">Attach to Form 1040, 1040-SR, 1040-NR, or 1041; partnerships must generally file Form 1065.</div>
              <div className="text-[9px]">Go to www.irs.gov/ScheduleC for instructions and the latest information.</div>
            </div>
            <div className="w-24 border-l border-black p-2 text-center">
              <div className="text-[9px]">OMB No. 1545-0074</div>
              <div className="font-bold text-2xl mt-1">{taxYear}</div>
              <div className="text-[8px]">Attachment</div>
              <div className="text-[8px]">Sequence No. 09</div>
            </div>
          </div>
        </div>

        {/* Name and SSN */}
        <div className="flex border-2 border-black mb-2">
          <div className="flex-1 border-r border-black p-2">
            <span className="text-[9px]">Name of proprietor</span>
            <div className="font-bold text-sm">{user?.real_name || user?.display_name || user?.name || user?.email || ''}</div>
          </div>
          <div className="w-44 p-2">
            <span className="text-[9px]">Social security number (SSN)</span>
            <div className="font-bold text-sm">XXX-XX-XXXX</div>
          </div>
        </div>

        {/* Part I & Part II in a single table for perfect column alignment */}
        <div className="border-2 border-black mb-2">
          <table className="w-full border-collapse text-[10px]" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '18px' }} />
              <col />
              <col style={{ width: '24px' }} />
              <col style={{ width: '95px' }} />
              <col style={{ width: '18px' }} />
              <col />
              <col style={{ width: '24px' }} />
              <col style={{ width: '95px' }} />
            </colgroup>
            <tbody>
              {/* ===== Part I: Income ===== */}
              <tr>
                <td colSpan="8" className="px-2 py-1 font-bold text-sm irs-header-bar" style={{ backgroundColor: '#000', color: '#fff' }}>Part I &nbsp;&nbsp; Income</td>
              </tr>
              <tr className="border-b border-gray-400">
                <td className="p-1 text-right font-bold align-top">1</td>
                <td colSpan="5" className="p-1">Gross receipts or sales. See instructions for line 1 and check the box if this income was reported to you on Form W-2 and the "Statutory employee" box on that form was checked &#9654; &#9744;</td>
                <td className="border-l border-black text-center font-bold align-middle">1</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line1)}</td>
              </tr>
              <tr className="border-b border-gray-400">
                <td className="p-1 text-right font-bold">2</td>
                <td colSpan="5" className="p-1">Returns and allowances</td>
                <td className="border-l border-black text-center font-bold">2</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line2)}</td>
              </tr>
              <tr className="border-b border-gray-400">
                <td className="p-1 text-right font-bold">3</td>
                <td colSpan="5" className="p-1">Subtract line 2 from line 1</td>
                <td className="border-l border-black text-center font-bold">3</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line3)}</td>
              </tr>
              <tr className="border-b border-gray-400">
                <td className="p-1 text-right font-bold">4</td>
                <td colSpan="5" className="p-1">Cost of goods sold (from line 42)</td>
                <td className="border-l border-black text-center font-bold">4</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line4)}</td>
              </tr>
              <tr className="border-b border-gray-400">
                <td className="p-1 text-right font-bold">5</td>
                <td colSpan="5" className="p-1"><strong>Gross profit.</strong> Subtract line 4 from line 3</td>
                <td className="border-l border-black text-center font-bold">5</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line5)}</td>
              </tr>
              <tr className="border-b border-gray-400">
                <td className="p-1 text-right font-bold">6</td>
                <td colSpan="5" className="p-1">Other income, including federal and state gasoline or fuel tax credit or refund (see instructions)</td>
                <td className="border-l border-black text-center font-bold">6</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line6)}</td>
              </tr>
              <tr className="border-b-2 border-black">
                <td className="p-1 text-right font-bold">7</td>
                <td colSpan="5" className="p-1"><strong>Gross income.</strong> Add lines 5 and 6</td>
                <td className="border-l border-black text-center font-bold">7</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px] bg-yellow-50">{fmt(line7)}</td>
              </tr>

              {/* ===== Part II: Expenses ===== */}
              <tr>
                <td colSpan="8" className="px-2 py-1 font-bold text-sm irs-header-bar" style={{ backgroundColor: '#000', color: '#fff' }}>Part II &nbsp;&nbsp; Expenses. Enter expenses for business use of your home only on line 30.</td>
              </tr>
              {/* Row 1: 8 / 18 */}
              <tr className="border-b border-gray-300">
                <td className="p-1 text-right font-bold">8</td>
                <td className="p-1">Advertising</td>
                <td className="border-l border-black text-center font-bold">8</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line8)}</td>
                <td className="border-l border-black p-1 text-right font-bold">18</td>
                <td className="p-1">Office expense</td>
                <td className="border-l border-black text-center font-bold">18</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line18)}</td>
              </tr>
              {/* Row 2: 9 / 19 */}
              <tr className="border-b border-gray-300">
                <td className="p-1 text-right font-bold">9</td>
                <td className="p-1">Car and truck expenses</td>
                <td className="border-l border-black text-center font-bold">9</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line9)}</td>
                <td className="border-l border-black p-1 text-right font-bold">19</td>
                <td className="p-1">Pension and profit-sharing plans</td>
                <td className="border-l border-black text-center font-bold">19</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line19)}</td>
              </tr>
              {/* Row 3: 10 / 20 (header) */}
              <tr className="border-b border-gray-300">
                <td className="p-1 text-right font-bold">10</td>
                <td className="p-1">Commissions and fees</td>
                <td className="border-l border-black text-center font-bold">10</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line10)}</td>
                <td className="border-l border-black p-1 text-right font-bold">20</td>
                <td className="p-1">Rent or lease:</td>
                <td className="border-l border-black"></td>
                <td className="border-l border-black"></td>
              </tr>
              {/* Row 4: 11 / 20a */}
              <tr className="border-b border-gray-300">
                <td className="p-1 text-right font-bold">11</td>
                <td className="p-1">Contract labor</td>
                <td className="border-l border-black text-center font-bold">11</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line11)}</td>
                <td className="border-l border-black p-1 text-right font-bold">a</td>
                <td className="p-1 pl-2">Vehicles, machinery, equipment</td>
                <td className="border-l border-black text-center font-bold">20a</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line20a)}</td>
              </tr>
              {/* Row 5: 12 / 20b */}
              <tr className="border-b border-gray-300">
                <td className="p-1 text-right font-bold">12</td>
                <td className="p-1">Depletion</td>
                <td className="border-l border-black text-center font-bold">12</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line12)}</td>
                <td className="border-l border-black p-1 text-right font-bold">b</td>
                <td className="p-1 pl-2">Other business property</td>
                <td className="border-l border-black text-center font-bold">20b</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line20b)}</td>
              </tr>
              {/* Row 6: 13 / 21 */}
              <tr className="border-b border-gray-300">
                <td className="p-1 text-right font-bold">13</td>
                <td className="p-1">Depreciation (see instructions)</td>
                <td className="border-l border-black text-center font-bold">13</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line13)}</td>
                <td className="border-l border-black p-1 text-right font-bold">21</td>
                <td className="p-1">Repairs and maintenance</td>
                <td className="border-l border-black text-center font-bold">21</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line21)}</td>
              </tr>
              {/* Row 7: 14 / 22 */}
              <tr className="border-b border-gray-300">
                <td className="p-1 text-right font-bold">14</td>
                <td className="p-1">Employee benefit programs</td>
                <td className="border-l border-black text-center font-bold">14</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line14)}</td>
                <td className="border-l border-black p-1 text-right font-bold">22</td>
                <td className="p-1">Supplies</td>
                <td className="border-l border-black text-center font-bold">22</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line22)}</td>
              </tr>
              {/* Row 8: 15 / 23 */}
              <tr className="border-b border-gray-300">
                <td className="p-1 text-right font-bold">15</td>
                <td className="p-1">Insurance (other than health)</td>
                <td className="border-l border-black text-center font-bold">15</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line15)}</td>
                <td className="border-l border-black p-1 text-right font-bold">23</td>
                <td className="p-1">Taxes and licenses</td>
                <td className="border-l border-black text-center font-bold">23</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line23)}</td>
              </tr>
              {/* Row 9: 16 Interest (header) / 24 Travel (header) */}
              <tr className="border-b border-gray-300">
                <td className="p-1 text-right font-bold">16</td>
                <td className="p-1">Interest:</td>
                <td className="border-l border-black"></td>
                <td className="border-l border-black"></td>
                <td className="border-l border-black p-1 text-right font-bold">24</td>
                <td className="p-1">Travel and meals:</td>
                <td className="border-l border-black"></td>
                <td className="border-l border-black"></td>
              </tr>
              {/* Row 10: 16a / 24a */}
              <tr className="border-b border-gray-300">
                <td className="p-1 text-right font-bold">a</td>
                <td className="p-1 pl-2">Mortgage (paid to banks, etc.)</td>
                <td className="border-l border-black text-center font-bold">16a</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line16a)}</td>
                <td className="border-l border-black p-1 text-right font-bold">a</td>
                <td className="p-1 pl-2">Travel</td>
                <td className="border-l border-black text-center font-bold">24a</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line24a)}</td>
              </tr>
              {/* Row 11: 16b / 24b */}
              <tr className="border-b border-gray-300">
                <td className="p-1 text-right font-bold">b</td>
                <td className="p-1 pl-2">Other</td>
                <td className="border-l border-black text-center font-bold">16b</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line16b)}</td>
                <td className="border-l border-black p-1 text-right font-bold">b</td>
                <td className="p-1 pl-2">Deductible meals</td>
                <td className="border-l border-black text-center font-bold">24b</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line24b)}</td>
              </tr>
              {/* Row 12: 17 / 25 */}
              <tr className="border-b border-gray-300">
                <td className="p-1 text-right font-bold">17</td>
                <td className="p-1">Legal and professional services</td>
                <td className="border-l border-black text-center font-bold">17</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line17)}</td>
                <td className="border-l border-black p-1 text-right font-bold">25</td>
                <td className="p-1">Utilities</td>
                <td className="border-l border-black text-center font-bold">25</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line25)}</td>
              </tr>
              {/* Row 13: (empty) / 26 */}
              <tr className="border-b border-gray-300">
                <td className="p-1"></td>
                <td className="p-1"></td>
                <td className="border-l border-black"></td>
                <td className="border-l border-black"></td>
                <td className="border-l border-black p-1 text-right font-bold">26</td>
                <td className="p-1">Wages (less employment credits)</td>
                <td className="border-l border-black text-center font-bold">26</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line26)}</td>
              </tr>
              {/* Row 14: (empty) / 27 */}
              <tr className="border-b border-gray-300">
                <td className="p-1"></td>
                <td className="p-1"></td>
                <td className="border-l border-black"></td>
                <td className="border-l border-black"></td>
                <td className="border-l border-black p-1 text-right font-bold">27</td>
                <td className="p-1">Other expenses (from line 48)</td>
                <td className="border-l border-black text-center font-bold">27b</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line27b)}</td>
              </tr>

              {/* ===== Summary rows (28-31) ===== */}
              <tr className="border-t-2 border-black border-b border-gray-400">
                <td className="p-1 text-right font-bold">28</td>
                <td colSpan="5" className="p-1"><strong>Total expenses</strong> before expenses for business use of home. Add lines 8 through 27b</td>
                <td className="border-l border-black text-center font-bold">28</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line28)}</td>
              </tr>
              <tr className="border-b border-gray-400">
                <td className="p-1 text-right font-bold">29</td>
                <td colSpan="5" className="p-1"><strong>Tentative profit or (loss).</strong> Subtract line 28 from line 7</td>
                <td className="border-l border-black text-center font-bold">29</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line29)}</td>
              </tr>
              <tr className="border-b border-gray-400">
                <td className="p-1 text-right font-bold">30</td>
                <td colSpan="5" className="p-1">Expenses for business use of your home. Attach Form 8829 unless using the simplified method.</td>
                <td className="border-l border-black text-center font-bold">30</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line30)}</td>
              </tr>
              <tr className="bg-yellow-50">
                <td className="p-1 text-right font-bold">31</td>
                <td colSpan="5" className="p-1"><strong>Net profit or (loss).</strong> Subtract line 30 from line 29.</td>
                <td className="border-l border-black text-center font-bold">31</td>
                <td className="border-l border-black p-1 text-right font-mono font-bold text-[12px]">{fmt(line31)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-3 text-[9px] text-center text-gray-600 print:mt-2">
          <div>Generated by RevTracker - For reference only. Verify all amounts before filing.</div>
          <div>Tax Year: {taxYear} | Generated: {new Date().toLocaleDateString()}</div>
        </div>
      </div>

      {/* Force black text for all form content, override dark theme */}
      <style>{`
        [data-testid="irs-print-form"],
        [data-testid="irs-print-form"] * {
          color: #000 !important;
        }
        [data-testid="irs-print-form"] .irs-header-bar,
        [data-testid="irs-print-form"] .irs-header-bar * {
          color: #fff !important;
        }
        @media print {
          /* Ensure the IRS form is the only thing visible and positioned correctly */
          [data-testid="irs-print-form"] {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
            background: white !important;
            z-index: 99999 !important;
          }
          /* Hide the sticky toolbar */
          [data-testid="irs-print-form"] .print\\:hidden {
            display: none !important;
          }
          /* Force text and background colors to print exactly as on screen */
          [data-testid="irs-print-form"] .irs-header-bar {
            background-color: #000 !important;
            color: #fff !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          [data-testid="irs-print-form"] .irs-header-bar * {
            color: #fff !important;
          }
          [data-testid="irs-print-form"] .bg-yellow-50 {
            background-color: #fefce8 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          [data-testid="irs-print-form"] .border-black {
            border-color: #000 !important;
          }
        }
      `}</style>
    </div>
  );
}
